import { Dispatch, SetStateAction, useEffect } from 'react';

import { FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import useTimetablesStore from '@calendar/store/useTimetablesStore';
import Event from '@event/components/Event';
import useEventTypesStore from '@event/store/useEventTypesStore';
import { CreateEvent } from '@event/types';
import usePopupStore from '@popup/stores/usePopupStore';
import Meta from '@seo/components/Meta';

export interface CreateEventComponentProps {
  register: UseFormRegister<CreateEvent>;
  setValue: UseFormSetValue<CreateEvent>;
  setPhotoId: Dispatch<SetStateAction<number>>;
  photoId: number;
  formState: FormState<CreateEvent>;
}

const CreateEventComponent = ({
  register,
  setValue,
  setPhotoId,
  photoId,
  formState,
}: CreateEventComponentProps) => {
  const { timetables } = useTimetablesStore();

  const { selectedEventType } = useEventTypesStore();
  const { popupState, setPopupState } = usePopupStore();

  const {
    isEventImageSelectorOpen,
    isTimeLengthSlotSelectorOpen,
    isEventSummaryShown,
    isSubmitEventShown,
    isEventTimeSlotsModalOpen,
    isLeavePromptModalOpen,
  } = popupState;

  const shouldShowEventSummary =
    !isEventImageSelectorOpen &&
    !isTimeLengthSlotSelectorOpen &&
    !isEventSummaryShown &&
    !isSubmitEventShown &&
    !isEventTimeSlotsModalOpen &&
    !isLeavePromptModalOpen &&
    timetables.length > 0;

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    if (timetables.length !== 0 || formState.isDirty) {
      window.addEventListener('beforeunload', handler);
    }

    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [formState.isDirty, timetables.length]);

  useEffect(() => {
    if (shouldShowEventSummary) {
      setPopupState({ isEventSummaryShown: true });
    }
  }, [setPopupState, shouldShowEventSummary]);

  useEffect(() => {
    if (timetables.length === 0) {
      setPopupState({ isSubmitEventShown: false });
      setPopupState({ isEventSummaryShown: false });
    }
  }, [setPopupState, timetables.length]);

  return (
    <>
      <Meta
        noIndex={true}
        noFollow={true}
        title="Vytvořit událost | Půjdu.cz"
        description="Na Půjdu.cz hlasujete o termínech a zjistíte ideální čas pro posezení u piva, sport nebo oslavu. Užijte si společné chvíle s přáteli bez zbytečného stresu."
        favicon={`/images/event-icons/${selectedEventType?.name}/${photoId}.ico`}
        isIconsWithResolutions={false}
      />
      <Event
        register={register}
        setValue={setValue}
        formState={formState}
        photoId={photoId}
        setPhotoId={setPhotoId}
      />
    </>
  );
};

export default CreateEventComponent;
